import { useRef, useState, useCallback } from "react";
import { debounce } from "../../../other/utils/otherUtils.jsx";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GooglePlaceDetails {
  /** Full formatted address string from Google (e.g. "2999 E Evesham Rd Unit 12, Voorhees Township, NJ 08043, USA") */
  formattedAddress: string;
  lat: number;
  lon: number;
  /** Street-level address only, constructed from addressComponents (e.g. "2999 E Evesham Rd Unit 12") */
  streetAddress?: string;
  city?: string;
  stateOrRegion?: string;
  postalCode?: string;
  country?: string;
  /** Display name of the place when it is a named location (business, POI, etc.) */
  propertyName?: string;
}

export interface IUseSwivuLocationReturn {
  searchResults: { label: string; value: string }[];
  onSearchAddress: (searchValue: string) => void;
  fetchGooglePlaceDetails: (placeId: string) => Promise<GooglePlaceDetails>;
  isLoading: boolean;
  clearSearchResults: () => void;
}

// ─── Address component parsing ────────────────────────────────────────────────

type GoogleAddressComponent = {
  longText: string;
  shortText: string;
  types: string[];
};

function parseAddressComponents(
  components: GoogleAddressComponent[]
): Pick<GooglePlaceDetails, "streetAddress" | "city" | "stateOrRegion" | "postalCode" | "country"> {
  const find = (type: string) => components.find((c) => c.types.includes(type));

  // Street-level parts
  const streetNumber = find("street_number")?.longText ?? "";
  const route = find("route")?.longText ?? "";
  const subpremise = find("subpremise")?.longText ?? "";

  const streetBase = [streetNumber, route].filter(Boolean).join(" ");
  const streetAddress = [streetBase, subpremise].filter(Boolean).join(", ") || undefined;

  // City: prefer locality, fall back to sublocality_level_1, then administrative_area_level_2
  const city =
    find("locality")?.longText ??
    find("sublocality_level_1")?.longText ??
    find("administrative_area_level_2")?.longText;

  // State uses shortText (e.g. "CA" instead of "California")
  const stateOrRegion = find("administrative_area_level_1")?.shortText;

  const postalCode = find("postal_code")?.longText;
  const country = find("country")?.longText;

  return {
    ...(streetAddress && { streetAddress }),
    ...(city && { city }),
    ...(stateOrRegion && { stateOrRegion }),
    ...(postalCode && { postalCode }),
    ...(country && { country }),
  };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

const BASE_REQUEST = {
  language: "en-US",
  includedRegionCodes: ["US"],
};

export const useSwivuLocation = (): IUseSwivuLocationReturn => {
  const isInit = useRef(false);
  const googlePlacesSession = useRef<unknown>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const googlePlaceSuggestions = useRef<Map<string, any>>(new Map());
  const [searchResults, setSearchResults] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ─── Session management ────────────────────────────────────────────────────

  const init = useCallback(() => {
    isInit.current = true;
  }, []);

  // Initialize once Google Maps is loaded
  if (typeof window !== "undefined") {
    // @ts-expect-error Google Maps typings missing
    if (window.googleMapsLoaded && !isInit.current) {
      init();
    } else if (!isInit.current) {
      window.addEventListener("googleMapsLoaded", init, { once: true });
    }
  }

  const startSession = () => {
    if (!googlePlacesSession.current) {
      // @ts-expect-error Google Maps typings missing
      googlePlacesSession.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  const endSession = () => {
    googlePlacesSession.current = null;
  };

  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    googlePlaceSuggestions.current = new Map();
  }, []);

  // ─── Search ───────────────────────────────────────────────────────────────

  const fetchAddress = async (value: string) => {
    if (!isInit.current || !value.trim()) return;

    startSession();
    setIsLoading(true);

    const request = {
      ...BASE_REQUEST,
      input: value,
      sessionToken: googlePlacesSession.current,
    };

    try {
      // @ts-expect-error Google Maps typings missing
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formatted = suggestions.map((placeInstance: any) => {
        if (!placeInstance.placePrediction) return { label: "", value: "" };
        googlePlaceSuggestions.current.set(
          placeInstance.placePrediction.placeId,
          placeInstance
        );
        return {
          label: placeInstance.placePrediction.text.toString(),
          value: placeInstance.placePrediction.placeId,
        };
      });

      setSearchResults(formatted);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      clearSearchResults();
    } finally {
      setIsLoading(false);
    }
  };

  // `fetchAddress` is defined in the same scope and is stable — safe to omit from deps.
  // eslint-disable-next-line
  const debouncedFetchAddress = useCallback(
    debounce(async (searchValue: string) => {
      await fetchAddress(searchValue);
    }, 300),
    []
  );

  const onSearchAddress = useCallback(
    (searchValue: string) => {
      setIsLoading(true);
      clearSearchResults();
      if (!searchValue.trim()) {
        setIsLoading(false);
        return;
      }
      debouncedFetchAddress(searchValue);
    },
    [clearSearchResults, debouncedFetchAddress]
  );

  // ─── Place detail resolution ──────────────────────────────────────────────

  const fetchGooglePlaceDetails = useCallback(
    async (placeId: string): Promise<GooglePlaceDetails> => {
      const placeInstance = googlePlaceSuggestions.current.get(placeId);
      if (!placeInstance) {
        throw new Error(`Google place instance not found for placeId: ${placeId}`);
      }

      const place = placeInstance.placePrediction.toPlace();

      await place.fetchFields({
        fields: ["formattedAddress", "addressComponents", "location", "displayName"],
      });

      const lat: number = place.location?.lat() ?? 0;
      const lon: number = place.location?.lng() ?? 0;
      const formattedAddress: string =
        place.formattedAddress ?? placeInstance.placePrediction.text.toString();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawComponents: any[] = place.addressComponents ?? [];
      const parsed = parseAddressComponents(rawComponents);

      // In the JS Places Library, place.displayName is a plain string (not the REST
      // API object { text, languageCode }). Use it as propertyName only when the place
      // is a named location (business, POI, etc.) — identified by not starting with a digit.
      const displayNameText: string = (place.displayName as string | null) ?? "";
      const propertyName =
        displayNameText && !/^\d/.test(displayNameText) ? displayNameText : undefined;

      endSession();

      return {
        formattedAddress,
        lat,
        lon,
        ...(propertyName && { propertyName }),
        ...parsed,
      };
    },
    []
  );

  return {
    searchResults,
    onSearchAddress,
    fetchGooglePlaceDetails,
    isLoading,
    clearSearchResults,
  };
};
